import { assert, subTestContext, Test } from "oc-e2e-testrunner";
import { v2 } from "oc-threejs/short3";
import { resetArrangement } from "./utils";
import { streckenZuGraph } from "../arrangement/grundrissFunctions";
import { schiebetorMasse } from "../arrangement3d/schiebetor/erzeugeSchiebetorRahmen";
import { ZElement } from "../arrangement/ZElement";
import { Arrangement } from "../arrangement/Arrangement";

/**
 * Testet die Platzierung von Schiebetoren auf nicht-trivialen Geometrien (Rechteck/Ecke) sowie
 * über verschiedene Breiten hinweg. Bisher war nur die gerade Strecke und ein einzelnes Rechteck
 * abgedeckt.
 *
 * Bewusst werden hier keine festen Warn-Erwartungen verglichen (Ecksituationen können legitim
 * warnen) – geprüft wird die geometrische Wohlgeformtheit des erzeugten Schiebetores.
 */
export default {
  name: "TestSchiebetorPlatzierung",
};

const find = (k: string) => APP.richMetadata.findAuswahlwert(k);

function assertSchiebetorWohlgeformt(tor: ZElement, ctx = "") {
  assert.isTrue(tor.isSchiebetor, `${ctx}ist Schiebetor`);
  assert.isTrue(tor.lichteWeite > 0, `${ctx}lichteWeite > 0`);
  const def = tor.schiebetorRahmenDef;
  assert.isNotNull(def, `${ctx}RahmenDef vorhanden`);
  assert.isTrue(def.torBreite > tor.breite, `${ctx}torBreite (mit Auslauf) > breite`);
  assert.isTrue(def.füllungsFenster.length >= 1, `${ctx}mind. ein Füllungsfenster`);
  assert.isTrue(def.oeffnungsplatzbedarf > tor.lichteWeite, `${ctx}Öffnungsplatzbedarf > lichteWeite`);
}

// ---------------------------------------------------------------------------
// 1) Schiebetor in einem Rechteck-Segment
// ---------------------------------------------------------------------------
export const testSchiebetorImRechteck: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement as Arrangement;
  agt.grundriss.setStandardForm("Rechteck", [12, 12], [true, true, true, true]);
  agt.update3d();
  const s = agt.segmente[0];
  agt.setSelectedObjects(s.getEmpties());
  await agt.changeSelectedZaunElemente({ massAuswahlwert: find("Schiebetor_Easy") });
  agt.update3d();
  const tor = agt.getTore().find((t) => t.isSchiebetor);
  assertSchiebetorWohlgeformt(tor);
};

// ---------------------------------------------------------------------------
// 2) Schiebetor im breiten Segment zwischen zwei Ecken (Kasten / U-Form)
// ---------------------------------------------------------------------------
export const testSchiebetorImKastenZwischenEcken: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement as Arrangement;
  // U-Form: zwei kurze Seiten links/rechts und ein breites Bodensegment dazwischen
  agt.grundriss.ecken = streckenZuGraph([
    [v2(0, 0), v2(0, 3)],
    [v2(0, 0), v2(10, 0)],
    [v2(10, 0), v2(10, 3)],
  ]).ecken;
  agt.update3d();
  // breitestes Segment = das Bodensegment zwischen den Ecken
  const s = [...agt.segmente].sort((a, b) => b.breite - a.breite)[0];
  agt.setSelectedObjects(s.getEmpties());
  await agt.changeSelectedZaunElemente({ massAuswahlwert: find("Schiebetor_Easy") });
  agt.update3d();
  const tor = agt.getTore().find((t) => t.isSchiebetor);
  assertSchiebetorWohlgeformt(tor, "Kasten: ");
  // Die angrenzenden Pfosten bleiben auch zwischen Ecken Schiebetorpfosten.
  tor.getPfosten()
    .filter((p) => p != null)
    .forEach((p) =>
      assert.isTrue(p.isSchiebetorpfosten, `Pfosten ${p.meta?.Key} muss Schiebetorpfosten sein`)
    );
};

// ---------------------------------------------------------------------------
// 3) Verschiedene Breiten: RahmenDef konsistent mit der reinen Berechnung
// ---------------------------------------------------------------------------
export const testSchiebetorVerschiedeneBreiten: Test = async () => {
  let letzteFächer = 0;
  for (const breite of [3, 5, 7, 12]) {
    await subTestContext(`Strecke ${breite}m`, async () => {
      resetArrangement();
      const agt = APP.arrangement as Arrangement;
      agt.grundriss.setStandardForm("Strecke", [breite]);
      agt.update3d();
      agt.setSelectedObjects(agt.getEmpties());
      await agt.changeSelectedZaunElemente({ massAuswahlwert: find("Schiebetor_Easy") });
      agt.update3d();
      const tor = agt.getTore().find((t) => t.isSchiebetor);
      assertSchiebetorWohlgeformt(tor, `${breite}m: `);

      const m = schiebetorMasse({
        lichteWeite: tor.schiebetorRahmenDef.lichteWeite,
        torHöhe: tor.höhe,
      });
      assert.equals(m.torBreite, tor.schiebetorRahmenDef.torBreite, "torBreite konsistent");
      assert.equals(
        m.oeffnungsplatzbedarf,
        tor.schiebetorRahmenDef.oeffnungsplatzbedarf,
        "Öffnungsplatzbedarf konsistent"
      );

      // breitere Tore haben tendenziell nicht weniger Fächer
      const fächer = tor.schiebetorRahmenDef.füllungsFenster.length;
      assert.isTrue(fächer >= letzteFächer, "Fächeranzahl wächst monoton mit der Breite");
      letzteFächer = fächer;
    });
  }
};
